import { useDebounce } from "@/hooks/useDebounce";
import React, { useEffect, useState } from "react";
import {
  FieldValues,
  useController,
  UseControllerProps,
} from "react-hook-form";
import { Input } from "../Input";

type InputProps = Omit<
  React.ComponentProps<typeof Input>,
  "name" | "value" | "onChange"
> & {
  enableDebounce?: boolean;
};

interface FormInputProps<TFieldValues extends FieldValues> extends InputProps {
  name: UseControllerProps<TFieldValues>["name"];
  control: UseControllerProps<TFieldValues>["control"];
}

const FormInput = <TFieldValues extends FieldValues>({
  name,
  control,
  enableDebounce,
  ...rest
}: FormInputProps<TFieldValues>) => {
  const { field, fieldState } = useController({
    name,
    control,
  });

  // If enableDebounce is true, debounce the input value with a delay of 300ms
  const [internalValue, setInternalValue] = useState("");
  const debounceSearch = useDebounce(internalValue.trim(), 300);
  useEffect(() => {
    if (enableDebounce) {
      field.onChange(debounceSearch);
    }
  }, [debounceSearch, enableDebounce]);

  // Incase clear input from outside (like reset form), we need to update internalValue
  useEffect(() => {
    if (enableDebounce && field.value === "") {
      setInternalValue("");
    }
  }, [field.value, enableDebounce]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (enableDebounce) {
      setInternalValue(e.target.value);
      return;
    }
    field.onChange(e);
  };

  return (
    <Input
      {...rest}
      {...field}
      error={fieldState.error?.message}
      value={enableDebounce ? internalValue : field.value}
      onChange={handleInputChange}
    />
  );
};

export { FormInput };
